import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { fortniteProducts, featuredProducts } from '../../mocks/fortniteProducts';
import { useCart } from '../../hooks/useCart';

export default function ProductPage() {
  const { id } = useParams();
  const { addToCart, getTotalItems } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  // Find product from both arrays
  const allProducts = [...fortniteProducts, ...featuredProducts];
  const product = allProducts.find(p => p.id === id);

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header cartItemsCount={getTotalItems()} />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Product Not Found</h1>
            <Link to="/" className="text-purple-400 hover:text-purple-300">
              Return to Home
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart({
        id: product.id,
        title: product.title,
        price: product.price,
        image: product.image
      });
    }
  };

  // Mock additional images for gallery
  const productImages = [
    product.image,
    `https://public.readdy.ai/ai/img_res/74dc8596d29c9ac8c39286b3a5e9c75c.jpg$%7Bproduct.title%7D%20in%20action%2C%20vibrant%20gaming%20interface%2C%20epic%20battle%20royale%20scene%20with%20purple%20and%20blue%20effects%2C%20high%20quality%20game%20graphics&width=600&height=400&seq=${product.id}-2&orientation=landscape`,
    `https://readdy.ai/api/search-image?query=Fortnite%20$%7Bproduct.title%7D%20detailed%20view%2C%20gaming%20currency%20interface%2C%20premium%20game%20content%20showcase%2C%20modern%20gaming%20UI%20design%20with%20neon%20effects&width=600&height=400&seq=${product.id}-3&orientation=landscape`,
    `https://readdy.ai/api/search-image?query=Epic%20Games%20Fortnite%20$%7Bproduct.title%7D%20promotional%20image%2C%20gaming%20marketplace%20style%2C%20professional%20product%20showcase%20with%20dark%20background&width=600&height=400&seq=${product.id}-4&orientation=landscape`
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      <Header cartItemsCount={getTotalItems()} />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-400 mb-8">
          <Link to="/" className="hover:text-white cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-white">{product.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div>
            <div className="mb-4">
              <img
                src={productImages[selectedImage]}
                alt={product.title}
                className="w-full h-96 object-cover rounded-lg"
              />
            </div>
            <div className="grid grid-cols-4 gap-2">
              {productImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`border-2 rounded-lg overflow-hidden cursor-pointer ${
                    selectedImage === index ? 'border-purple-400' : 'border-gray-700'
                  }`}
                >
                  <img
                    src={image}
                    alt={`${product.title} ${index + 1}`}
                    className="w-full h-20 object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div>
            <h1 className="text-3xl font-bold text-white mb-4">{product.title}</h1>
            
            <div className="flex items-center gap-4 mb-6">
              <div className="text-4xl font-bold text-yellow-400 mb-6">
                €{product.price}
              </div>
              {product.originalPrice && (
                <span className="text-xl text-gray-400 line-through">${product.originalPrice}</span>
              )}
              {product.discount && (
                <span className="bg-red-500 text-white px-2 py-1 rounded text-sm font-semibold">
                  -{product.discount}%
                </span>
              )}
            </div>

            <div className="bg-gray-800 rounded-lg p-4 mb-6">
              <h3 className="text-white font-semibold mb-2">Product Description</h3>
              <p className="text-gray-300 leading-relaxed">
                {product.category === 'currency' && 'Get instant V-Bucks to purchase new outfits, gliders, pickaxes, emotes, wraps and the latest season Battle Pass! V-Bucks are an in-game currency that can be spent in Fortnite Battle Royale, Creative, and Save the World modes.'}
                {product.category === 'battlepass' && 'Unlock over 100 rewards with the Fortnite Battle Pass! Get exclusive outfits, back blings, harvesting tools, emotes, wraps, and more. Level up your Battle Pass by playing matches and completing challenges.'}
                {product.category === 'subscription' && 'Join Fortnite Crew and get access to the current season Battle Pass, 1,000 V-Bucks monthly, and an exclusive Crew Pack with outfit, back bling, and more! Cancel anytime.'}
                {product.category === 'giftcard' && 'Epic Games Store gift cards can be used to purchase games, add-ons, and other content from the Epic Games Store. Perfect for gifting to friends and family who love gaming.'}
              </p>
            </div>

            <div className="bg-gray-800 rounded-lg p-4 mb-6">
              <h3 className="text-white font-semibold mb-3">Key Features</h3>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-400"></i>
                  Instant digital delivery
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-400"></i>
                  Compatible with all platforms
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-400"></i>
                  No expiration date
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-check-line text-green-400"></i>
                  24/7 customer support
                </li>
              </ul>
            </div>

            <div className="bg-gray-800 rounded-lg p-4 mb-6">
              <h3 className="text-white font-semibold mb-3">System Requirements</h3>
              <div className="text-gray-300 text-sm">
                <p><strong>Platforms:</strong> PC, Mac, PlayStation, Xbox, Nintendo Switch, iOS, Android</p>
                <p><strong>Internet:</strong> Broadband connection required</p>
                <p><strong>Account:</strong> Epic Games account required</p>
              </div>
            </div>

            {/* Quantity and Add to Cart */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-2">
                <span className="text-white font-semibold">Quantity:</span>
                <div className="flex items-center border border-gray-600 rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="px-3 py-2 text-white hover:bg-gray-700 cursor-pointer"
                  >
                    -
                  </button>
                  <span className="px-4 py-2 text-white bg-gray-800">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="px-3 py-2 text-white hover:bg-gray-700 cursor-pointer"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <Button
                onClick={handleAddToCart}
                className="flex-1"
                size="lg"
              >
                <i className="ri-shopping-cart-line mr-2"></i>
                Add to Cart - ${(product.price * quantity).toFixed(2)}
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="px-6"
              >
                <i className="ri-heart-line"></i>
              </Button>
            </div>

            <div className="mt-6 flex items-center gap-4 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <i className="ri-shield-check-line text-green-400"></i>
                Secure Payment
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-blue-400"></i>
                Instant Delivery
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-customer-service-line text-purple-400"></i>
                24/7 Support
              </div>
            </div>
          </div>
        </div>

        {/* Related Products */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-white mb-8">Related Products</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {allProducts
              .filter(p => p.id !== product.id && p.category === product.category)
              .slice(0, 4)
              .map((relatedProduct) => (
                <Link
                  key={relatedProduct.id}
                  to={`/product/${relatedProduct.id}`}
                  className="group cursor-pointer"
                >
                  <div className="bg-gray-800 rounded-lg overflow-hidden hover:bg-gray-700 transition-colors">
                    <img
                      src={relatedProduct.image}
                      alt={relatedProduct.title}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-4">
                      <h3 className="text-white font-semibold text-sm mb-2 line-clamp-2">
                        {relatedProduct.title}
                      </h3>
                      <div className="flex items-center justify-between">
                        <span className="text-purple-400 font-bold">${relatedProduct.price}</span>
                        {relatedProduct.discount && (
                          <span className="bg-red-500 text-white px-2 py-1 rounded text-xs">
                            -{relatedProduct.discount}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
